import EditIcon from '@/components/icons/EditIcon';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type {
  SurveyAnswerOption,
  SurveyAnswerOptionInput,
  SurveyQuestionInput,
  SurveyTemplate,
  SurveyTemplateInput,
} from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { Loader2, Play, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useChannel } from 'ably/react';
import { useChannelName } from '@/routes/AblyAuth';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import type { AppError } from '@/features/auth/types';
import { mapHttpError } from '@/lib/mapApiError';
import {
  createSurveyAnswerOption,
  deleteSurveyAnswerOption,
  deleteSurveyTemplate,
  fetchSurveyTemplate,
  listSurveyTemplates,
  updateSurveyAnswerOption,
  updateSurveyQuestion,
  updateSurveyTemplate,
} from '@/features/webinar/webinarThunks';
import { fetchSurveyResults } from '@/features/live/liveThunks';
import SurveyForm from '../../create-webinar/live/survey/SurveyForm';
import type { LiveSessionResourceStateDto, TriggerEventInput } from '@/features/live/types';
import SurveyCard from '@/components/common/asset/SurveyCard';
import Loader from '@/components/ui/Loader';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';

export default function SurveysList({
  surveys,
  visualIdentity,
  workspaceId,
  webinarId,
  sessionId,
  adminResourceStates,
  handleTriggerEvent,
  liveParticipantCountSync,
}: {
  surveys: SurveyTemplate[];
  loading: boolean;
  visualIdentity: WorkspaceVisualIdentity;
  workspaceId: number;
  webinarId: number;
  sessionId?: number;
  adminResourceStates: LiveSessionResourceStateDto[];
  handleTriggerEvent?: (event: TriggerEventInput) => void;
  liveParticipantCountSync?: number;
}) {
  const dispatch = useAppDispatch();
  const surveyResultsByTemplateId = useAppSelector(
    (s: RootState) => s.live.surveyResultsByTemplateId ?? {},
  );
  const { t: tCommon } = useTranslation('common');
  const { t: tWebinars } = useTranslation('webinars');
  const [selectedSurvey, setSelectedSurvey] = useState<SurveyTemplate | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loadingSurveyId, setLoadingSurveyId] = useState<number | null>(null);
  const [surveysShowingResults, setSurveysShowingResults] = useState<Set<number>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEditSurvey = (survey: any) => {
    setSelectedSurvey(survey);
    setIsEditModalOpen(true);
  };

  const channelName = useChannelName();
  useChannel(channelName ?? '', (message) => {
    if (!channelName) return;
    const data: any = message.data;
    if (
      message.name === 'live-event' &&
      data &&
      typeof data === 'object' &&
      data.resourceType === 'SURVEY_TEMPLATE' &&
      (data.eventType === 'SHOW_SURVEY_RESPONSE' || data.eventType === 'HIDE_SURVEY_RESPONSE') &&
      typeof data.resourceId === 'number'
    ) {
      setSurveysShowingResults((prev) => {
        const newSet = new Set(prev);
        if (data.eventType === 'SHOW_SURVEY_RESPONSE') {
          newSet.add(data.resourceId);
        } else {
          newSet.delete(data.resourceId);
        }
        return newSet;
      });
    }
  });

  /*  const handleShowSurveyResults = async (survey: SurveyTemplate) => {
    if (!handleTriggerEvent) return;

    const isShowingResults = surveysShowingResults.has(survey.id);

    setLoadingSurveyId(survey.id);
    try {
      if (isShowingResults) {
        await handleTriggerEvent({
          eventType: 'HIDE_SURVEY_RESPONSE',
          resourceType: 'SURVEY_TEMPLATE',
          resourceId: survey.id,
          payloadJson: JSON.stringify({
            timeNow: new Date().getTime(),
          }),
        });
      } else {
        await handleTriggerEvent({
          eventType: 'SHOW_SURVEY_RESPONSE',
          resourceType: 'SURVEY_TEMPLATE',
          resourceId: survey.id,
          payloadJson: JSON.stringify({
            timeNow: new Date().getTime(),
          }),
        });
      }

      setSurveysShowingResults((prev) => {
        const newSet = new Set(prev);
        if (isShowingResults) {
          newSet.delete(survey.id);
        } else {
          newSet.add(survey.id);
        }
        return newSet;
      });
    } catch (error) {
      console.error('Error toggling survey results:', error);
    } finally {
      setLoadingSurveyId(null);
    }
  }; */

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedSurvey(null);
  };

  const handleToggleSurveyLive = async (survey: SurveyTemplate) => {
    if (!handleTriggerEvent) return;

    const isSurveyLive = adminResourceStates.some(
      (state) =>
        state.resourceType === 'SURVEY_TEMPLATE' && state.resourceId === survey.id && state.live,
    );

    setLoadingSurveyId(survey.id);
    try {
      if (isSurveyLive) {
        await handleTriggerEvent({
          eventType: 'RESOURCE_HIDE',
          resourceType: 'SURVEY_TEMPLATE',
          resourceId: survey.id,
          payloadJson: JSON.stringify({
            timeNow: new Date().getTime(),
          }),
        });
        if (webinarId && workspaceId && sessionId) {
          void dispatch(
            fetchSurveyResults({
              workspaceId,
              webinarId,
              templateId: survey.id,
              sessionId,
            }),
          );
        }
      } else {
        await handleTriggerEvent({
          eventType: 'RESOURCE_SHOW',
          resourceType: 'SURVEY_TEMPLATE',
          resourceId: survey.id,
          payloadJson: JSON.stringify({
            timeNow: new Date().getTime(),
          }),
        });
      }
    } catch (error) {
      console.error('Error toggling survey live:', error);
    } finally {
      setLoadingSurveyId(null);
    }
  };

  const handleDeleteSurvey = async () => {
    if (!selectedSurvey?.id) {
      console.error('No survey selected');
      return;
    }

    const confirmed = window.confirm(tWebinars('adminLive.editModal.surveyDeleteConfirm'));

    if (!confirmed) {
      return;
    }

    try {
      await dispatch(
        deleteSurveyTemplate({
          workspaceId,
          webinarId,
          templateId: selectedSurvey.id,
        }),
      ).unwrap();

      await dispatch(listSurveyTemplates({ workspaceId, webinarId })).unwrap();

      handleCloseModal();
    } catch (error) {
      console.error('Error deleting survey:', error);
    }
  };

  const handleSaveFromModal = () => {
    if ((window as any).surveyFormSave) {
      (window as any).surveyFormSave();
    }
  };

  const getColorValueById = (
    colorId: string | undefined,
    customColor: string | undefined,
    defaultColor: string,
    visualIdentity: WorkspaceVisualIdentity | null | undefined,
  ): string => {
    if (!colorId || colorId === 'custom') {
      return customColor || defaultColor;
    }

    if (/^#[0-9A-Fa-f]{6}$/.test(colorId)) {
      return colorId;
    }

    if (visualIdentity) {
      if (colorId === 'accent') {
        return visualIdentity.accentColor || defaultColor;
      }
      if (colorId === 'background') {
        return visualIdentity.backgroundColor || defaultColor;
      }
      if (colorId === 'contrast') {
        return visualIdentity.contrastColor || defaultColor;
      }
      if (colorId === 'secondary-background') {
        return visualIdentity.secondaryBackgroundColor || defaultColor;
      }
      if (colorId === 'gradient1-color1') {
        return 'gradient1';
      }
      if (colorId === 'gradient2-color1') {
        return 'gradient2';
      }
    }

    if (colorId === 'black') {
      return '#000000';
    }
    if (colorId === 'white') {
      return '#FFFFFF';
    }

    return defaultColor;
  };

  const resolveColorValue = (
    value?: string,
    custom?: string | null,
    visualIdentity?: WorkspaceVisualIdentity | null,
  ) => {
    if (!value) return null;
    if (value === 'custom') {
      return custom ?? null;
    }
    if (value === 'no-border') {
      return null;
    }

    return getColorValueById(value, custom ?? undefined, '#000000', visualIdentity);
  };

  const handleSurveySubmit = async (surveyData: any) => {
    if (!selectedSurvey?.id) {
      console.error('No survey selected');
      return;
    }

    setIsSubmitting(true);
    try {
      const templateId = selectedSurvey.id;

      const backgroundColor = resolveColorValue(
        surveyData.backgroundColor,
        surveyData.customBackgroundColor,
        visualIdentity,
      );
      const borderColor = resolveColorValue(
        surveyData.borderColor,
        surveyData.customBorderColor,
        visualIdentity,
      );
      const accentColor = resolveColorValue(
        surveyData.accentColor,
        surveyData.customAccentColor,
        visualIdentity,
      );
      const titleColor = surveyData.titleColor || '#000000';

      const surveyTemplateInput: SurveyTemplateInput = {
        title: surveyData.title,
        backgroundColor: backgroundColor || null,
        borderColor: borderColor,
        accentColor: accentColor || null,
        titleColor: titleColor,
        waitingRoomSurvey: false,
      };

      await dispatch(
        updateSurveyTemplate({
          workspaceId,
          webinarId,
          templateId,
          input: surveyTemplateInput,
        }),
      ).unwrap();

      const currentSurvey = await dispatch(
        fetchSurveyTemplate({
          workspaceId,
          webinarId,
          templateId,
        }),
      ).unwrap();

      const existingQuestion = currentSurvey?.questions?.[0];
      if (existingQuestion?.id) {
        const questionInput: SurveyQuestionInput = {
          textContent: surveyData.question.textContent,
          type: surveyData.question.type,
          position: surveyData.question.position ?? 0,
          required: surveyData.question.required ?? false,
          maxSelections: surveyData.question.maxSelections ?? null,
          randomizeOptions: surveyData.question.randomizeOptions ?? false,
        };

        await dispatch(
          updateSurveyQuestion({
            workspaceId,
            webinarId,
            templateId,
            questionId: existingQuestion.id,
            input: questionInput,
          }),
        ).unwrap();
      }

      const existingServerOptions = currentSurvey?.questions?.[0]?.options || [];
      const existingOptionMap = new Map<number, SurveyAnswerOption>(
        existingServerOptions.map((opt) => [opt.id, opt]),
      );
      const existingOptionTextMap = new Map<string, SurveyAnswerOption>(
        existingServerOptions.map((opt) => [opt.textContent.trim().toLowerCase(), opt]),
      );

      const processedOptionIds = new Set<number>();
      const processedOptionTexts = new Set<string>();
      const questionId = existingQuestion?.id;

      if (!questionId) {
        console.error('Question ID not found');
        return;
      }

      for (const option of surveyData.question.options || []) {
        if (!option.textContent?.trim()) {
          continue;
        }

        const normalizedText = option.textContent.trim().toLowerCase();

        if (option.id && processedOptionIds.has(option.id)) {
          processedOptionTexts.add(normalizedText);
          continue;
        }

        if (processedOptionTexts.has(normalizedText)) {
          continue;
        }

        const trimmedTextContent = option.textContent.trim();
        const calculatedPosition = option.position ?? processedOptionIds.size;

        if (option.id && existingOptionMap.has(option.id)) {
          const serverOption = existingOptionMap.get(option.id)!;
          const textContentChanged = serverOption.textContent.trim() !== trimmedTextContent;
          const positionChanged = serverOption.position !== calculatedPosition;

          if (textContentChanged || positionChanged) {
            const optionInput: SurveyAnswerOptionInput = {
              textContent: trimmedTextContent,
              position: calculatedPosition,
            };

            await dispatch(
              updateSurveyAnswerOption({
                workspaceId,
                webinarId,
                templateId,
                questionId,
                optionId: option.id,
                input: optionInput,
              }),
            ).unwrap();
          }
          processedOptionIds.add(option.id);
          processedOptionTexts.add(normalizedText);
        } else if (!option.id) {
          const existingByText = existingOptionTextMap.get(normalizedText);

          if (existingByText) {
            processedOptionIds.add(existingByText.id);
            processedOptionTexts.add(normalizedText);
          } else {
            const optionInput: SurveyAnswerOptionInput = {
              textContent: trimmedTextContent,
              position: calculatedPosition,
            };

            const optionId = await dispatch(
              createSurveyAnswerOption({
                workspaceId,
                webinarId,
                templateId,
                questionId,
                input: optionInput,
              }),
            ).unwrap();

            if (optionId) {
              processedOptionIds.add(optionId);
              processedOptionTexts.add(normalizedText);
            }
          }
        }
      }

      for (const serverOption of existingServerOptions) {
        if (!processedOptionIds.has(serverOption.id)) {
          await dispatch(
            deleteSurveyAnswerOption({
              workspaceId,
              webinarId,
              templateId,
              questionId,
              optionId: serverOption.id,
            }),
          ).unwrap();
        }
      }

      const updatedSurvey = await dispatch(
        fetchSurveyTemplate({
          workspaceId,
          webinarId,
          templateId,
        }),
      ).unwrap();

      if (handleTriggerEvent) {
        await handleTriggerEvent({
          eventType: 'RESOURCE_UPDATE',
          resourceType: 'SURVEY_TEMPLATE',
          resourceId: updatedSurvey.id,
        });
      }

      setSelectedSurvey(updatedSurvey);
      toast.success(tWebinars('adminLive.editModal.surveySaved'));
    } catch (error) {
      console.error('Error updating survey:', error);

      const appError = error as AppError | undefined;

      if (appError?.message) {
        const mainMessage = tCommon(appError.message, appError.message);
        toast.error(mainMessage);
      } else if (error instanceof Error && error.message) {
        toast.error(tCommon('errors.generic', error.message));
      } else {
        toast.error(tCommon('errors.generic', 'Une erreur est survenue lors de la sauvegarde.'));
      }

      if (appError?.details && Array.isArray(appError.details)) {
        appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
          if (detail.message) {
            const mappedMessage = mapHttpError(detail.message);
            const translatedMessage = tCommon(mappedMessage, detail.message);
            toast.error(translatedMessage);
          }
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-4"
      style={{ color: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000' }}
    >
      {surveys?.map((survey) => {
        const isSurveyLive = adminResourceStates.some(
          (state) =>
            state.resourceType === 'SURVEY_TEMPLATE' &&
            state.resourceId === survey.id &&
            state.live,
        );

        return (
          <div key={survey.id} className="w-full">
            <p className="text-sm font-medium pb-4">{survey.title}</p>

            <div className=" flex  gap-[10px] ">
              {/* Survey */}
              <SurveyCard
                survey={survey}
                visualIdentity={visualIdentity}
                isAttendee={false}
                isAdmin
                isModerator
                isShow={isSurveyLive}
                showResults={
                  surveysShowingResults.has(survey.id) || !!surveyResultsByTemplateId[survey.id]
                }
                workspaceId={workspaceId}
                webinarId={webinarId}
                sessionId={sessionId}
                surveyResults={surveyResultsByTemplateId[survey.id] ?? null}
                liveParticipantCountSync={liveParticipantCountSync}
              />
              {/* settings */}
              <div className="flex flex-col gap-4 ">
                <button
                  className="rounded-lg p-3 cursor-pointer"
                  onClick={() => handleToggleSurveyLive(survey)}
                  disabled={loadingSurveyId === survey.id}
                  style={{
                    backgroundColor: isSurveyLive
                      ? '#FFD753'
                      : visualIdentity?.contrastColor || '#2356CC',
                  }}
                >
                  {loadingSurveyId === survey.id ? (
                    <Loader2
                      size={18}
                      color={isSurveyLive ? '#000000' : '#FFFFFF'}
                      className="animate-spin"
                    />
                  ) : isSurveyLive ? (
                    <X size={18} color="#000000" />
                  ) : (
                    <Play size={18} color="#FFFFFF" />
                  )}
                </button>
                {/*  <button
                  className="rounded-lg p-3 cursor-pointer disabled:bg-medium disabled:cursor-not-allowed"
                  disabled={!isSurveyLive || loadingSurveyId === survey.id}
                  onClick={() => handleShowSurveyResults(survey)}
                  style={{
                    backgroundColor: surveysShowingResults.has(survey.id)
                      ? '#FFD753'
                      : visualIdentity?.contrastColor || '#2356CC',
                  }}
                >
                  <Eye
                    size={18}
                    color={
                      !isSurveyLive || loadingSurveyId === survey.id
                        ? '#C5C5CA'
                        : surveysShowingResults.has(survey.id)
                          ? '#000000'
                          : '#FFFFFF'
                    }
                  />
                </button> */}
                <button
                  className="rounded-lg p-3 cursor-pointer disabled:bg-medium disabled:cursor-not-allowed"
                  disabled={isSurveyLive || loadingSurveyId === survey.id}
                  onClick={() => handleEditSurvey(survey)}
                  style={{
                    backgroundColor: visualIdentity?.contrastColor || '#2356CC',
                  }}
                >
                  <EditIcon
                    size={18}
                    color={isSurveyLive || loadingSurveyId === survey.id ? '#C5C5CA' : '#FFFFFF'}
                  />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Modal d'édition du sondage */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-medium" hideCloseButton>
          <DialogHeader className="hidden">
            <DialogTitle>{tWebinars('adminLive.editModal.surveyTitle')}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between">
            <p className="text-text-secondary">{tWebinars('adminLive.editModal.tools')}</p>
            <div className="flex items-center gap-6">
              <button
                type="button"
                onClick={handleDeleteSurvey}
                className="bg-white rounded-lg py-3 px-2 border border-input cursor-pointer"
              >
                <Trash2 className="size-6" />
              </button>

              <button type="button" onClick={handleCloseModal} className=" cursor-pointer">
                {tWebinars('adminLive.editModal.cancel')}
              </button>

              <Button onClick={handleSaveFromModal} disabled={isSubmitting}>
                {isSubmitting ? <Loader /> : tWebinars('adminLive.editModal.save')}
              </Button>
            </div>
          </div>
          {selectedSurvey && (
            <SurveyForm
              surveyIndex={0}
              // surveyId={selectedSurvey.id}
              onRemove={() => {}}
              onRemoveAnswerOption={() => {}}
              canRemove={false}
              onSubmit={handleSurveySubmit}
              existingSurvey={selectedSurvey}
              isModal={true}
              onSave={handleSaveFromModal}
              onDelete={handleDeleteSurvey}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
